import { type MeResponse, type UserTier } from '@diario/common';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { iconoDeRol, roleDesdeTier } from '../../../components/ui/badge';

export interface SelloRolProps {
	/** La sesión activa: de aquí salen el `tier`/`systemRole` que deciden icono y nombre del rol —
	 * mismas dos fuentes que antes calculaba `InsigniaSesion` dentro de `cabecera.tsx`. */
	sesion: MeResponse;
}

/** `sesion.tier` o `'ADMIN'` para quien tiene ese rol de sistema — las mismas dos fuentes que
 * decide el `variant` visual (`roleDesdeTier`). */
type ClaveSello = UserTier | 'ADMIN';

/**
 * El sello de rol de la cabecera (ADR-0022): sustituye al `Badge` de tier por el patrón de las 80
 * pantallas del cliente — el icono de marca vendorizado del rol (`iconoDeRol`, `public/brand/`) más
 * el nombre del rol en tinta, nunca el color de rol crudo como texto (regla de contraste,
 * `docs/design-system.md`). Componente presentacional puro: no toca ningún dato de servidor más
 * allá de la sesión que recibe por prop.
 *
 * H-05 (antes resuelto por `Badge.compact`, retirado en esta misma tarea al perder su único
 * consumidor): por debajo de `sm` el nombre queda oculto (`hidden sm:inline`, solo el icono) y el
 * `aria-label` de la raíz mantiene el nombre accesible completo con independencia del ancho — el
 * nombre accesible nunca depende del viewport.
 */
export const SelloRol = ({ sesion }: SelloRolProps): React.JSX.Element => {
	const { t } = useTranslation('shell');
	const variante = roleDesdeTier(sesion.tier, sesion.systemRole);
	const clave: ClaveSello = sesion.systemRole === 'ADMIN' ? 'ADMIN' : sesion.tier;

	/** Llamadas `t()` literales, una por clave — nunca indexando la clave `sello` con una plantilla
	 * de cadena a partir de `clave`: con `ClaveSello` cerrado y corto (los seis `UserTier` más
	 * `'ADMIN'`), el `Record` evita registrar un patrón nuevo en `preservePatterns` de
	 * `i18next.config.ts` (fichero fuera de la lista de esta tarea) — mismo molde que
	 * `fichaSolicitud.tsx` usa para los arquetipos (`docs/lessons.md` § «Una clave i18n indexada
	 * por variable no la ve el extractor, pero un `Record` de llamadas literales sí»). */
	const etiquetaPorSello: Record<ClaveSello, string> = {
		ADMIN: t('sello.ADMIN'),
		COLLABORATOR: t('sello.COLLABORATOR'),
		GRUMETE: t('sello.GRUMETE'),
		LEGACY: t('sello.LEGACY'),
		LITE: t('sello.LITE'),
		PENDING: t('sello.PENDING'),
		STANDARD: t('sello.STANDARD'),
	};
	const etiqueta = etiquetaPorSello[clave];
	const icono = iconoDeRol(variante);

	return (
		<span aria-label={etiqueta} className='inline-flex items-center gap-1.5' role='img'>
			{renderizarIcono(icono)}
			<span className='hidden font-bold text-ink text-xs uppercase tracking-label sm:inline'>{etiqueta}</span>
		</span>
	);
};

/**
 * `icono` es una URL de `public/brand/` (member/grumete/pendiente/official) o el glifo `⬡` de
 * colaborador (`iconoDeRol`) — nunca los dos casos a la vez, así que se resuelve en una función
 * aparte (no un ternario anidado, `lint/style/noNestedTernary`) para que TypeScript estreche
 * `icono` a `string` dentro de cada rama, igual que `renderizarIcono` de `badge.tsx`. `accent`/
 * `neutral` (p. ej. `ADMIN`) no tienen sello propio: `iconoDeRol` devuelve `undefined` y aquí no se
 * pinta nada.
 */
function renderizarIcono(icono: string | undefined): ReactNode {
	if (!icono) {
		return null;
	}
	if (icono.startsWith('/') || icono.startsWith('http')) {
		return <img alt='' className='h-4 w-4 shrink-0 object-contain' src={icono} />;
	}
	return (
		<span aria-hidden='true' className='shrink-0'>
			{icono}
		</span>
	);
}
